import type { IQuestion, IQuestionSet, ISetSubQuestion, ITavolaImage, PercorsoIntervista } from '../models';

// Perimetro fissato all'avvio (interview_question_set_app + junction), solo ciò che serve al routing.
export interface IRoutingContext {
  percorso: PercorsoIntervista;
  ambienti: readonly string[];
  impianti: readonly string[];
}

// Regola di attivazione (Specifica Rev. 1 §8, dipendenze in OR dal 23/09/2026): una voce è
// attiva se e solo se
//   (1) il percorso scelto è tra i suoi percorsi;
//   (2) ambienti e impianti della voce sono entrambi vuoti, OPPURE almeno un suo ambiente è tra
//       i coinvolti, OPPURE almeno un suo impianto è tra quelli selezionati.
// La Rev. 1 legava ambienti e impianti in AND; il foglio Domande v5 li elenca in un'unica colonna
// "si fa solo se è coinvolto…" (es. 4.1 Luce: Impianto elettrico; Soggiorno; Camera), quindi basta
// uno qualsiasi. Percorso COMPLETA: (2) non si applica (matrice §8: D3 sì, D8 8.1-8.12, D10
// 10.0-10.11, tutto). Resta (1): PE1 è "solo parziale" e in completa non compare. Stessa cosa per
// il percorso VELOCE (04/11/2026): niente controllo delle stanze, contano solo i percorsi della voce.
// (La regola "impianto pesante → D10 con tutte le stanze" del blocco F è caduta il 22/09/2026
// insieme al tag leggero/pesante.)
export function isActive(v: ISetSubQuestion, ctx: IRoutingContext): boolean {
  if (!v.percorsi.some((p) => p.code === ctx.percorso)) {
    return false;
  }
  if ('completa' === ctx.percorso || 'veloce' === ctx.percorso || (0 === v.ambienti.length && 0 === v.impianti.length)) {
    return true;
  }
  return v.ambienti.some((a) => ctx.ambienti.includes(a)) || v.impianti.some((i) => ctx.impianti.includes(i));
}

// Testo della voce nel percorso: il sintetico se per QUEL percorso è marcato is_sintetico ed esiste.
export function textFor(v: ISetSubQuestion, percorso: PercorsoIntervista): string {
  const p = v.percorsi.find((x) => x.code === percorso);
  const sint = v.text_sintetico?.trim() ?? '';
  return true === p?.is_sintetico && '' !== sint ? sint : v.text;
}

// Immagini di una voce visiva → card dell'intervista. imgUrl risolve image_path (bucket) in URL.
export function cardImages(v: ISetSubQuestion, imgUrl: (path: string) => string): ITavolaImage[] | null {
  return v.is_image ? v.images.map((im) => ({ id: im.id, src: imgUrl(im.image_path), meaning: im.caption ?? '' })) : null;
}

// Set pubblicato → domande dell'intervista (IQuestion[]), solo voci attive, in sort_order.
// id = posizione 1..N fra le attive (etichetta "Domanda N di M"); l'identità è `code`.
export function activeQuestions(
  set: IQuestionSet,
  ctx: IRoutingContext,
  imgUrl: (path: string) => string = (p) => p,
): IQuestion[] {
  const bySort = <T extends { sort_order: number }>(a: T, b: T): number => a.sort_order - b.sort_order;
  return [...set.questions]
    .sort(bySort)
    .filter((q) => isActive(q, ctx))
    .map((q, i) => {
      const subs = [...q.sub_questions].sort(bySort).filter((s) => isActive(s, ctx));
      return {
        id: i + 1,
        code: q.code,
        tag: q.tag,
        title: q.title,
        text: textFor(q, ctx.percorso),
        hasTextbox: q.has_textbox,
        isImage: q.is_image,
        sondaggi: subs.map((s) => textFor(s, ctx.percorso)),
        subCodes: subs.map((s) => s.code),
        subImages: subs.map((s) => cardImages(s, imgUrl)),
        subTextbox: subs.map((s) => s.has_textbox),
      };
    });
}

// code → question_app.id di ogni voce del set (madri e sottodomande). È la chiave con cui
// interview_answer_app referenzia la voce risposta: la proiezione IQuestion porta il code, non
// l'uuid, e lo snapshot nemmeno. Le righe del set sono immutabili per versione, quindi la mappa
// di una versione fissata non cambia mai sotto i piedi.
export function questionIdsByCode(set: IQuestionSet): Record<string, string> {
  const out: Record<string, string> = {};
  set.questions.forEach((q) => {
    out[q.code] = q.id;
    q.sub_questions.forEach((sq) => {
      out[sq.code] = sq.id;
    });
  });
  return out;
}
